import { PageHeader, PageShell } from '@/components/PageShell';
import { JsonViewer } from '@/components/JsonViewer';
import { ContentLoading } from '@/components/SuspenseTrap';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCompanyImage } from '@/db/useCompanyImage';
import { companyDetailsState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDateTime, formatRelative } from '@/util/dates';
import { CompanyDao } from '@rock/db-admin';
import type { Company, CompanyId } from '@rock/types';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

const companyDao = new CompanyDao();

const DetailItem: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <span className="break-all text-base text-foreground">{value ?? '—'}</span>
    </div>
);

const RelativeDate: FC<{ iso: string | undefined }> = ({ iso }) =>
    iso ? <span title={formatDateTime(iso)}>{formatRelative(iso)}</span> : null;

const CompanyDetailsPage: FC = () => {
    const { companyId: rawCompanyId } = useParams<{ companyId: string }>();
    const companyId = useMemo(
        () => (rawCompanyId ? (decodeURIComponent(rawCompanyId) as CompanyId) : undefined),
        [rawCompanyId],
    );

    // Loadable, not a bare read: a suspend here unmounts the page under the layout's shared
    // boundary and the atom refetches on remount. `loading` keeps the not-found card honest.
    const [company, companyLoadable] = useLoadableAtomValue(companyDetailsState(companyId));
    const mapImageUrl = useCompanyImage(company, 'map');

    const [isRefreshing, setIsRefreshing] = useState(false);
    const [logoOverride, setLogoOverride] = useState('');
    const [isSavingOverride, setIsSavingOverride] = useState(false);

    async function handleRefresh() {
        if (!companyId) return;
        setIsRefreshing(true);
        try {
            const fn = httpsCallable<{ companyId: CompanyId }, { company: Company }>(getFunctions(), 'refreshCompany');
            await fn({ companyId });
            toast.success('Company refreshed');
        } catch (err) {
            toast.error('Failed to refresh company', {
                description: err instanceof Error ? err.message : 'Unknown error',
            });
        } finally {
            setIsRefreshing(false);
        }
    }

    async function handleSaveOverride() {
        if (!companyId) return;
        const next = logoOverride.trim();
        setIsSavingOverride(true);
        try {
            await companyDao.setLogoOverride(companyId, next || undefined);
            toast.success(next ? 'Logo override saved' : 'Logo override cleared');
            setLogoOverride('');
        } catch (err) {
            toast.error('Failed to save override', {
                description: err instanceof Error ? err.message : 'Unknown error',
            });
        } finally {
            setIsSavingOverride(false);
        }
    }

    const displayedLogoUrl = company?.logoOverrideUrl ?? company?.brand?.logoUrl ?? company?.brand?.iconUrl;

    return (
        <PageShell width="medium">
            <PageHeader title="Company details" id={companyId} backLabel="Back to companies" />

            {companyLoadable.state === 'loading' ? (
                <ContentLoading />
            ) : !company ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Company not found</CardTitle>
                        <CardDescription>
                            We could not find a document for this company. It may have been deleted.
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <>
                    <Card>
                        <CardHeader>
                            <CardTitle>Logo</CardTitle>
                            <CardDescription>
                                Manual override takes precedence over Brandfetch and Google photos.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                                {displayedLogoUrl ? (
                                    <img
                                        src={displayedLogoUrl}
                                        alt={`${company.name} logo`}
                                        className="h-16 w-auto rounded border bg-white object-contain p-2"
                                    />
                                ) : (
                                    <span className="text-sm text-muted-foreground">No logo.</span>
                                )}
                                <div className="text-sm text-muted-foreground">
                                    <div>Override: {company.logoOverrideUrl ? 'yes' : 'no'}</div>
                                    <div>
                                        Brandfetch:{' '}
                                        {company.brand?.logoUrl ? 'yes' : company.brand?.notFound ? 'not found' : 'no'}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Input
                                    placeholder="Paste a logo URL to override…"
                                    value={logoOverride}
                                    onChange={(e) => setLogoOverride(e.target.value)}
                                    disabled={isSavingOverride}
                                />
                                <Button onClick={handleSaveOverride} disabled={isSavingOverride}>
                                    Save override
                                </Button>
                                {company.logoOverrideUrl && (
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setLogoOverride('');
                                            void companyDao.setLogoOverride(company.id, undefined);
                                            toast.success('Override cleared');
                                        }}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Summary</CardTitle>
                                    <CardDescription>Fields sourced from Google Places.</CardDescription>
                                </div>
                                <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                                    <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                                    {isRefreshing ? 'Refreshing…' : 'Refresh'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <DetailItem label="Name" value={company.name} />
                                <DetailItem label="Phone" value={company.phone} />
                                <DetailItem label="Website" value={company.website} />
                                <DetailItem label="Domain" value={company.domain} />
                                <DetailItem label="Address" value={company.address?.formatted} />
                                <DetailItem label="Place types" value={company.googlePlaceTypes?.join(', ')} />
                                <DetailItem
                                    label="Google refreshed"
                                    value={<RelativeDate iso={company.googleRefreshedAt} />}
                                />
                                <DetailItem label="Next refresh" value={<RelativeDate iso={company.nextRefreshAt} />} />
                                <DetailItem
                                    label="Brand fetched"
                                    value={<RelativeDate iso={company.brand?.fetchedAt} />}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {!!company.geo && (
                        <Card>
                            <CardHeader>
                                <CardTitle>Location</CardTitle>
                                <CardDescription>
                                    Static Google Map preview (proxied through the cloud function to keep the API key
                                    server-side).
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {mapImageUrl ? (
                                    <img
                                        src={mapImageUrl}
                                        alt={`${company.name} map`}
                                        className="w-full max-w-xl rounded border bg-white"
                                    />
                                ) : (
                                    <span className="text-sm text-muted-foreground">Loading map…</span>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    <Card>
                        <CardHeader>
                            <CardTitle>Object</CardTitle>
                            <CardDescription>Raw Firestore document.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                                <JsonViewer data={company} />
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </PageShell>
    );
};

export default CompanyDetailsPage;
